// Ultimate Card Game (UCG) — Tactical Deck Builder Modal
// Zero-page-reload integrated modal for deck creation, editing, and inspection.

import { CARDS_DATA, getActiveCards } from '../data/cards.js';
import { getCardThumbUrl, preloadAllCardThumbnails, PRELOADED_CARD_THUMBS } from '../data/cardThumbUrl.js';
import { loadCustomDecks, saveCustomDecks, makeCustomDeckId, deriveDominantRace, getDeckValidity, DECK_ICON_PRESETS, MAX_CUSTOM_DECKS, MIN_DECK_SIZE, MAX_COPIES_PER_CARD, getMaxCopiesForCard } from '../data/customDecks.js';
import { getFactionCrestMarkup } from './factionCrestMarkup.js';
import { REEL_3_STYLES, REEL_3_STYLES_MAP } from '../engine/slotMachine.js';
import { inventory } from '../engine/inventory.js';
import { getDisplayName } from '../net/playerIdentity.js';
import { getIconSvg } from './icons.js';

// Debounce window between an edit (add/remove card, rename) and the
// autosave write to localStorage - long enough to coalesce a burst of
// clicks/keystrokes into one write, short enough that flushAutosave() (see
// close()/openDeck()/createNewDeck()) rarely has anything pending to flush.
const AUTOSAVE_DEBOUNCE_MS = 350;

// Synthesized Sound Effects
class TacticalAudio {
  constructor(game = null) {
    this.game = game;
    this.ctx = null;
  }

  isMuted() {
    if (this.game?.audio && typeof this.game.audio.isMuted === 'boolean') {
      return this.game.audio.isMuted;
    }
    if (typeof window !== 'undefined' && window.tcgGame?.audio && typeof window.tcgGame.audio.isMuted === 'boolean') {
      return window.tcgGame.audio.isMuted;
    }
    try {
      if (typeof localStorage !== 'undefined') {
        const stored = localStorage.getItem('ucg_sound_muted');
        if (stored !== null) return stored === 'true';
      }
    } catch {}
    return true;
  }

  getVolume() {
    if (this.game?.audio?.sfxVolume !== undefined) {
      return this.game.audio.sfxVolume;
    }
    if (typeof window !== 'undefined' && window.tcgGame?.audio?.sfxVolume !== undefined) {
      return window.tcgGame.audio.sfxVolume;
    }
    return 0.6;
  }

  init() {
    if (this.isMuted()) return;
    if (!this.ctx) {
      if (this.game?.audio?.ctx) {
        this.ctx = this.game.audio.ctx;
      } else if (typeof window !== 'undefined' && window.tcgGame?.audio?.ctx) {
        this.ctx = window.tcgGame.audio.ctx;
      } else {
        const AudioCtx = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;
        if (AudioCtx) this.ctx = new AudioCtx();
      }
    }
  }

  playClick() {
    if (this.isMuted()) return;
    try {
      this.init();
      if (!this.ctx) return;
      const vol = this.getVolume();
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(840, this.ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(420, this.ctx.currentTime + 0.05);
      gain.gain.setValueAtTime(0.04 * vol, this.ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + 0.05);
      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start();
      osc.stop(this.ctx.currentTime + 0.05);
    } catch {}
  }

  playAdd() {
    if (this.isMuted()) return;
    try {
      this.init();
      if (!this.ctx) return;
      const vol = this.getVolume();
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(440, this.ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(880, this.ctx.currentTime + 0.08);
      gain.gain.setValueAtTime(0.06 * vol, this.ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + 0.08);
      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start();
      osc.stop(this.ctx.currentTime + 0.08);
    } catch {}
  }

  playRemove() {
    if (this.isMuted()) return;
    try {
      this.init();
      if (!this.ctx) return;
      const vol = this.getVolume();
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(480, this.ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(240, this.ctx.currentTime + 0.08);
      gain.gain.setValueAtTime(0.06 * vol, this.ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + 0.08);
      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start();
      osc.stop(this.ctx.currentTime + 0.08);
    } catch {}
  }

  playSuccess() {
    if (this.isMuted()) return;
    try {
      this.init();
      if (!this.ctx) return;
      const vol = this.getVolume();
      const now = this.ctx.currentTime;
      [523.25, 659.25, 783.99].forEach((freq, idx) => {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.05 * vol, now + idx * 0.06);
        gain.gain.linearRampToValueAtTime(0, now + idx * 0.06 + 0.12);
        osc.connect(gain);
        gain.connect(this.ctx.destination);
        osc.start(now + idx * 0.06);
        osc.stop(now + idx * 0.06 + 0.12);
      });
    } catch {}
  }
}

const WING_ICON_SVG = `
  <svg class="card-flying-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M3 13c3.5-6 9-8 17-9-2 5-5.5 8.5-10 10 3 0 6-.5 8-2-2.5 4-6.5 6-11 6-2 0-3.5-.5-4-5z"/>
  </svg>
`;

const UNIT_ICON_SVG = `
  <svg class="strip-type-svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M14.5 17.5 3 6V3h3l11.5 11.5" />
    <path d="m13 19 6-6" />
    <path d="m16 16 4 4" />
    <path d="m19 21 2-2" />
    <path d="M14.5 6.5 18 3h3v3l-11.5 11.5" />
    <path d="m5 14 6 6" />
    <path d="m7 17-4 4" />
    <path d="m3 19 2 2" />
  </svg>
`;

const STRUCTURE_ICON_SVG = `
  <svg class="strip-type-svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
`;

const SPELL_ICON_SVG = `
  <svg class="strip-type-svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" />
    <path d="m19 5-1 3-3 1 3 1 1 3 1-3 3-1-3-1Z" />
  </svg>
`;

function getCardTypeInfo(card) {
  if (card.isStructure || card.type === 'Structure') {
    return { type: 'structure', label: 'Structure', svg: STRUCTURE_ICON_SVG };
  }
  if (card.isSpell || card.type === 'Spell' || card.type === 'Module') {
    return { type: 'spell', label: 'Spell', svg: SPELL_ICON_SVG };
  }
  return { type: 'unit', label: 'Unit', svg: UNIT_ICON_SVG };
}

export function matchesCardSearch(card, query) {
  if (!query) return true;
  let q = query.toLowerCase().trim();
  // Normalize multi-word tier phrases like "tier 1", "tier 2", "tier 3"
  q = q.replace(/\btier\s+(\d)\b/gi, 'tier$1');
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;

  const cardType = getCardTypeInfo(card).type;
  const rarity = (card.rarity || (card.tier === 1 ? 'Common' : (card.tier === 2 ? 'Uncommon' : 'Epic'))).toLowerCase();
  const isAir = !!(card.isAir || card.elementType === 'Air');
  const isGround = !isAir && cardType !== 'spell';
  const race = (card.race || '').toLowerCase();
  const caste = (card.caste || '').toLowerCase();
  const name = (card.name || '').toLowerCase();
  const desc = (card.description || '').toLowerCase();
  const lore = (card.lore || '').toLowerCase();
  const attrs = (card.attributes || []).map(a => a.toLowerCase());
  const canAttack = (card.damage > 0 || card.isKamikaze) && cardType !== 'spell' && card.canAttack !== false;
  const isCloaked = !!(card.isCloaked || card.cloaked || card.stealth || card.isStealth) || desc.includes('cloaked melee');
  const isDetector = !!(card.isDetector || card.detectsCloak || card.globalDetector || card.detector);
  const hasHyper = card.race !== 'Sul' && !card.isSpell && !card.isStructure && CARDS_DATA.some(c =>
    (c.type === 'Melt' || c.isMelt) && c.name?.toLowerCase() === `hyper ${name}`
  );

  return tokens.every(rawToken => {
    let t = rawToken;
    if (t.startsWith('type:') || t.startsWith('t:')) {
      t = t.replace(/^(type|t):/, '');
    } else if (t.startsWith('is:')) {
      t = t.replace(/^is:/, '');
    } else if (t.startsWith('has:') || t.startsWith('attr:') || t.startsWith('attribute:')) {
      t = t.replace(/^(has|attr|attribute):/, '');
    } else if (t.startsWith('race:') || t.startsWith('faction:')) {
      t = t.replace(/^(race|faction):/, '');
    } else if (t.startsWith('rarity:') || t.startsWith('r:')) {
      t = t.replace(/^(rarity|r):/, '');
    }

    // Hyper / Triplet / Melt Fusion Keywords
    if (['hyper', 'triplet', 'tripple', 'triple', 'melt'].includes(t)) {
      return hasHyper || name.startsWith('hyper ') || desc.includes('hyper') || desc.includes('melt');
    }

    // Cost filters: cost:5, cost<10, cost>20, cost<=15, cost>=30
    const costMatch = t.match(/^(?:cost|plasma)([:=<>]=?|<=|>=)(\d+)$/);
    if (costMatch) {
      const op = costMatch[1];
      const val = parseInt(costMatch[2], 10);
      if (op === ':' || op === '=' || op === '==') return card.cost === val;
      if (op === '<') return card.cost < val;
      if (op === '<=') return card.cost <= val;
      if (op === '>') return card.cost > val;
      if (op === '>=') return card.cost >= val;
    }

    // Card Types & Modules / Base Modules
    if (['module', 'modules', 'modul', 'module', 'basis', 'base'].includes(t)) {
      return !!(card.isModule || card.type === 'Module' || card.moduleId);
    }
    if (['spell', 'spells', 'magic'].includes(t)) {
      return cardType === 'spell';
    }
    if (['structure', 'structures', 'building', 'buildings', 'turret', 'turrets'].includes(t)) {
      return cardType === 'structure';
    }
    if (['unit', 'units', 'troop', 'troops', 'minion', 'minions'].includes(t)) {
      return cardType === 'unit';
    }

    // Flight / Domain
    if (['flying', 'flyer', 'flyers', 'air', 'aerial'].includes(t)) {
      return isAir;
    }
    if (t === 'ground') {
      return isGround;
    }

    // Factions & Castes
    if (t === 'human' || t === 'terran') return race === 'human' || race === 'terran';
    if (t === 'sul') return race === 'sul';
    if (t === 'loaf' || t === 'loafs') return caste === 'loaf';
    if (t === 'pillar' || t === 'pillars') return caste === 'pillar';
    if (t === 'lock' || t === 'locks') return caste === 'lock';
    if (t === 'vulture' || t === 'vultures') return caste === 'vulture';

    // Rarities
    if (['common', 'uncommon', 'rare', 'epic'].includes(t)) {
      return rarity === t;
    }

    // Tiers
    const tierMatch = t.match(/^(?:t|tier)(\d)$/);
    if (tierMatch) {
      return card.tier === parseInt(tierMatch[1], 10);
    }

    // Targeting & Combat Roles
    if (t === 'anti-air' || t === 'antiair' || t === 'aa') {
      return canAttack && card.targetsAir !== false;
    }
    if (t === 'anti-ground' || t === 'antiground') {
      return canAttack && card.targetsGround !== false;
    }
    if (t === 'kamikaze' || t === 'suicide') {
      return !!card.isKamikaze || desc.includes('kamikaze') || desc.includes('suicide');
    }
    // Cloak / Stealth / Invisibility & Synonyms
    if ([
      'cloak', 'cloaked', 'cloaking', 'cloaks',
      'stealth', 'invisible', 'invisibility',
      'hidden', 'hide', 'camo', 'camouflage', 'camouflaged',
      'tarnung', 'getarnt', 'tarn', 'getarnte', 'getarnten',
      'unsichtbar', 'unsichtbarkeit',
      'ghost', 'geister', 'schatten', 'shade', 'shades'
    ].includes(t)) {
      return isCloaked || name.includes('shade') || (desc.includes('cloak') && !desc.includes('detect cloaked') && !desc.includes('detects cloaked')) || desc.includes('stealth') || desc.includes('hidden') || desc.includes('invisible');
    }

    // Detector / Detection / Radar & Synonyms
    if ([
      'detector', 'detectors', 'detect', 'detects', 'detecting', 'detection',
      'detektor', 'detektoren', 'radar', 'radars',
      'scanner', 'scanners', 'scan', 'scans', 'scanning',
      'sensor', 'sensors', 'reveal', 'reveals', 'revealer', 'revealing',
      'aufklärer', 'aufklaerer', 'aufklärung', 'aufklaerung',
      'enttarnen', 'enttarnt', 'enttarnung', 'sichtbar'
    ].includes(t)) {
      return isDetector || desc.includes('detect') || desc.includes('radar') || desc.includes('sensor') || desc.includes('reveal');
    }
    if (t === 'healer' || t === 'heal' || t === 'healing' || t === 'medic') {
      return !!card.isHealer || desc.includes('heal') || desc.includes('medic');
    }
    if (t === 'splash' || t === 'aoe') {
      return !!(card.splashRadius || card.splashLine) || desc.includes('splash') || desc.includes('aoe');
    }
    if (t === 'melee') {
      return card.projectileType === 'Melee' || desc.includes('melee');
    }
    if (t === 'ranged') {
      return (card.projectileType && card.projectileType !== 'Melee') || (card.range && card.range > 2);
    }

    // Attributes (Armor / Counter System)
    if (t === 'mech' || t === 'mechanical') {
      return attrs.includes('mechanical') || desc.includes('mech');
    }
    if (t === 'bio' || t === 'biological' || t === 'organic') {
      return attrs.includes('biological') || desc.includes('organic') || desc.includes('bio');
    }
    if (t === 'armored' || t === 'armor') {
      return attrs.includes('armored') || (card.armor && card.armor > 0) || desc.includes('armor');
    }
    if (t === 'massive' || t === 'heavy') {
      return attrs.includes('massive') || desc.includes('massive') || desc.includes('heavy');
    }
    if (t === 'light') {
      return attrs.includes('light') || desc.includes('light');
    }
    if (t === 'psionic' || t === 'psi') {
      return attrs.includes('psionic') || desc.includes('psionic');
    }

    // Projectiles
    if (t === 'bullet' || t === 'bullets' || t === 'ballistic') {
      return card.projectileType === 'Bullet' || desc.includes('bullet');
    }
    if (t === 'energy' || t === 'laser' || t === 'plasma') {
      return card.projectileType === 'Energy' || desc.includes('energy') || desc.includes('laser') || desc.includes('plasma');
    }
    if (t === 'grenade' || t === 'grenades') {
      return card.projectileType === 'Grenade' || desc.includes('grenade');
    }
    if (t === 'rocket' || t === 'rockets' || t === 'missile' || t === 'missiles') {
      return card.projectileType === 'Rocket' || desc.includes('rocket') || desc.includes('missile');
    }

    // Fallback: general substring search in name, description, lore, caste, race
    return name.includes(t) || desc.includes(t) || lore.includes(t) || caste.includes(t) || race.includes(t);
  });
}

export class DeckBuilderModal {
  constructor(game) {
    this.game = game;
    this.modalEl = document.getElementById('deckbuilder-modal');
    this.audio = new TacticalAudio(game);
    this.cards = getActiveCards();

    this.savedDecks = [];
    this.currentDeck = null;
    this.lastEditedDeckId = null;

    // Filters
    this.filterFaction = 'all'; // 'all' | 'Human' | 'Sul'
    this.filterRarity = 'all';  // 'all' | 'Common' | 'Uncommon' | 'Rare' | 'Epic'
    this.filterTier = 'all';    // 'all' | 1 | 2 | 3
    this.filterCost = 'all';    // 'all' | 'low' | 'mid' | 'high'
    this.filterFlying = false;
    this.filterAttackGround = false;
    this.filterAttackAir = false;
    this.searchQuery = '';

    this.cacheDom();
    this.bindEvents();
    this._prewarmCardThumbnails();
  }

  cacheDom() {
    if (!this.modalEl) return;
    this.dom = {
      // Exit button
      btnClose: document.getElementById('btn-close-deckbuilder'),

      // Search & Filters
      searchInput: document.getElementById('search-input'),
      searchClearBtn: document.getElementById('search-clear-btn'),
      searchWrapper: this.modalEl.querySelector('.search-wrapper'),
      factionBtns: this.modalEl.querySelectorAll('[data-faction]'),
      rarityBtns: this.modalEl.querySelectorAll('[data-rarity]'),
      tierBtns: this.modalEl.querySelectorAll('[data-tier]'),
      costBtns: this.modalEl.querySelectorAll('[data-cost]'),
      btnFilterFlying: document.getElementById('btn-filter-flying'),
      attackTargetBtns: this.modalEl.querySelectorAll('[data-attack-target]'),
      btnToggleFilters: document.getElementById('btn-toggle-filters'),
      filterSegment: document.getElementById('filter-segment'),
      filterToggleIcon: document.getElementById('filter-toggle-icon'),

      // Cards Grid
      cardsGrid: document.getElementById('cards-grid'),

      // Deck Panel (single view - editing whichever deck was opened; there
      // is no more internal "My Decks" list, see deleteCurrentDeck())
      deckPanel: document.getElementById('deck-panel'),
      deckViewEdit: document.getElementById('deck-view-edit'),
      drawerCloseEdit: document.getElementById('drawer-close-icon-edit'),

      // Deck Edit View Elements
      deckInput: document.getElementById('deck-name-input'),
      btnEditDeckName: document.getElementById('btn-edit-deck-name'),
      btnDeckIconPicker: document.getElementById('btn-deck-icon-picker'),
      deckIconPreview: document.getElementById('deck-icon-preview'),
      deckIconPopover: document.getElementById('deck-icon-popover'),
      btnDeckColorPicker: document.getElementById('btn-deck-color-picker'),
      deckColorPreview: document.getElementById('deck-color-preview'),
      deckColorPopover: document.getElementById('deck-color-popover'),
      deckCountBadge: document.getElementById('deck-count-badge'),
      deckValidityHint: document.getElementById('deck-validity-hint'),
      deckStrips: document.getElementById('deck-strips-list'),
      btnDecksNav: document.getElementById('btn-deck-overview'),
      btnDelete: document.getElementById('btn-delete-deck'),
      curveBars: document.getElementById('curve-bars-grid'),
      curveAvg: document.getElementById('curve-avg-val'),

      // In-UI dialog (replaces native alert()/confirm())
      dialogOverlay: document.getElementById('deckbuilder-dialog-overlay'),
      dialogBox: document.getElementById('deckbuilder-dialog-box'),
      dialogTitle: document.getElementById('deckbuilder-dialog-title'),
      dialogMessage: document.getElementById('deckbuilder-dialog-message'),
      dialogCancel: document.getElementById('deckbuilder-dialog-cancel'),
      dialogConfirm: document.getElementById('deckbuilder-dialog-confirm'),

      // Mobile Bottom Bar
      mobileBar: document.getElementById('mobile-bottom-bar'),
      mobileOpenPill: document.getElementById('mobile-open-pill'),
      mobileDeckLabel: document.getElementById('mobile-deck-label'),
      mobileDeckSub: document.getElementById('mobile-deck-sub'),
      drawerBackdrop: document.getElementById('drawer-backdrop'),
      drawerHandle: document.getElementById('drawer-drag-area')
    };
  }

  bindEvents() {
    if (!this.dom) return;

    // Exit Button ("BACK TO THE VOID")
    this.dom.btnClose?.addEventListener('click', () => {
      this.audio.playClick();
      const editedDeckId = this.currentDeck?.id || this.lastEditedDeckId;
      this.close();
      this.game.deckBuilder?.openModal({ selectDeckKey: editedDeckId });
    });

    // Toggle Filters (Collapsed on default)
    this.dom.btnToggleFilters?.addEventListener('click', () => {
      this.audio.playClick();
      const isOpen = this.dom.filterSegment?.classList.toggle('show-filters');
      this.dom.btnToggleFilters?.classList.toggle('open', isOpen);
      this.dom.btnToggleFilters?.setAttribute('aria-expanded', String(isOpen));
      if (this.dom.filterToggleIcon) {
        this.dom.filterToggleIcon.textContent = isOpen ? '▲' : '▼';
      }
      if (isOpen && this.isDesktop() && this.dom.searchInput) {
        this.dom.searchInput.focus();
        if (this.dom.searchInput.value) {
          this.dom.searchInput.select();
        }
      }
    });

    // Search Input & Clear Button
    this.dom.searchInput?.addEventListener('input', (e) => {
      this.searchQuery = e.target.value.toLowerCase().trim();
      this._updateSearchClearVisibility();
      this.renderCollection();
    });

    this.dom.searchInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.dom.searchInput.value) {
        e.preventDefault();
        e.stopPropagation();
        this.clearSearch();
      }
    });

    this.dom.searchClearBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.audio.playClick();
      this.clearSearch();
      if (this.dom.searchInput && this.isDesktop()) {
        this.dom.searchInput.focus();
      }
    });

    this._updateSearchClearVisibility();

    // Faction Filter
    this.dom.factionBtns?.forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.audio.playClick();
        this.dom.factionBtns.forEach(b => b.classList.remove('active'));
        e.currentTarget.classList.add('active');
        this.filterFaction = e.currentTarget.dataset.faction;
        this.renderCollection();
      });
    });

    // Rarity Filter
    this.dom.rarityBtns?.forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.audio.playClick();
        const rarity = e.currentTarget.dataset.rarity;
        this.setRarityFilter(rarity);
      });
    });

    // Tier Filter
    this.dom.tierBtns?.forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.audio.playClick();
        this.dom.tierBtns.forEach(b => b.classList.remove('active'));
        e.currentTarget.classList.add('active');
        this.filterTier = e.currentTarget.dataset.tier === 'all' ? 'all' : parseInt(e.currentTarget.dataset.tier, 10);
        this.renderCollection();
      });
    });

    // Cost Filter
    this.dom.costBtns?.forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.audio.playClick();
        this.dom.costBtns.forEach(b => b.classList.remove('active'));
        e.currentTarget.classList.add('active');
        this.filterCost = e.currentTarget.dataset.cost;
        this.renderCollection();
      });
    });

    // Flying Filter (Toggle Chip)
    this.dom.btnFilterFlying?.addEventListener('click', () => {
      this.audio.playClick();
      this.filterFlying = !this.filterFlying;
      this.dom.btnFilterFlying.classList.toggle('active', this.filterFlying);
      this.renderCollection();
    });

    // Attack Target Filter (Segmented & Multi-Select)
    this.dom.attackTargetBtns?.forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.audio.playClick();
        const target = e.currentTarget.dataset.attackTarget;
        this.setAttackTargetFilter(target);
      });
    });

    // Deck Icon Picker
    this.dom.btnDeckIconPicker?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.audio.playClick();
      this.toggleIconPopover();
    });

    // Deck Color Picker
    this.dom.btnDeckColorPicker?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.audio.playClick();
      this.toggleColorPopover();
    });

    document.addEventListener('click', (e) => {
      if (this.dom.deckIconPopover && !this.dom.deckIconPopover.classList.contains('hidden')) {
        if (!e.target.closest('.deck-icon-picker-wrap')) {
          this.dom.deckIconPopover.classList.add('hidden');
        }
      }
      if (this.dom.deckColorPopover && !this.dom.deckColorPopover.classList.contains('hidden')) {
        if (!e.target.closest('.deck-color-picker-wrap')) {
          this.dom.deckColorPopover.classList.add('hidden');
        }
      }
    });

    // Deck Name Editing
    this.dom.deckInput?.addEventListener('input', (e) => {
      if (this.currentDeck) {
        this.currentDeck.name = e.target.value || 'Untitled Deck';
        this.updateMobileBarEdit(this.getDeckStats());
        this.autosaveDeck();
      }
    });

    // Pencil icon next to the name: purely a "this is editable" affordance,
    // focuses the (always-editable) name input.
    this.dom.btnEditDeckName?.addEventListener('click', () => {
      this.audio.playClick();
      this.dom.deckInput?.focus();
      this.dom.deckInput?.select();
    });

    // "Decks" - replaces the old "Save Deck" button. Autosave already
    // persists every edit (see autosaveDeck()), so this is now pure
    // navigation back to the deck-selection screen, same destination as
    // "BACK TO THE VOID" and the main-menu/sidebar Decks buttons - close()
    // flushes any not-yet-written autosave on the way out.
    this.dom.btnDecksNav?.addEventListener('click', () => {
      this.audio.playClick();
      const editedDeckId = this.currentDeck?.id || this.lastEditedDeckId;
      this.close();
      this.game.deckBuilder?.openModal({ selectDeckKey: editedDeckId });
    });

    // Delete Deck (Confirmation dialog)
    this.dom.btnDelete?.addEventListener('click', async () => {
      if (!this.currentDeck) return;
      const ok = await this.showDialog({
        title: 'Delete Deck',
        message: `Are you sure you want to permanently delete deck "${this.currentDeck.name}"? This action cannot be undone.`,
        mode: 'confirm',
        danger: true,
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel'
      });
      if (ok) this.deleteCurrentDeck();
    });

    // Mobile Drawer
    this.dom.mobileBar?.addEventListener('click', () => this.setDrawer(true));
    this.dom.mobileOpenPill?.addEventListener('click', () => this.setDrawer(true));
    this.dom.drawerBackdrop?.addEventListener('click', () => this.setDrawer(false));
    this.dom.drawerCloseEdit?.addEventListener('click', () => this.setDrawer(false));

    // Swipe to close mobile drawer - the sheet's transform tracks the
    // finger live during the drag (instead of just snapping shut once a
    // fixed 50px threshold is crossed), so grabbing the handle actually
    // feels draggable the way a native bottom sheet does, per chat report
    // "aktuell hat man nicht das gefühl, dass es klappen könnte". Release
    // past ~30% of the panel height and it closes; otherwise it snaps
    // back open. .deck-panel.dragging (CSS) suspends the slide transition
    // and reads the live drag offset from the --drag-translate custom
    // property while it's set - a plain inline `transform` would lose to
    // .deck-panel.drawer-open's `transform ... !important` in the cascade,
    // but an !important rule can still read a custom property set inline.
    let dragStartY = 0;
    let dragDeltaY = 0;
    let dragging = false;

    const onDragStart = (e) => {
      dragStartY = e.touches[0].clientY;
      dragDeltaY = 0;
      dragging = true;
      this.dom.deckPanel?.classList.add('dragging');
      this.dom.drawerHandle?.classList.add('dragging');
    };
    const onDragMove = (e) => {
      if (!dragging) return;
      dragDeltaY = Math.max(0, e.touches[0].clientY - dragStartY);
      this.dom.deckPanel?.style.setProperty('--drag-translate', `${dragDeltaY}px`);
    };
    const onDragEnd = () => {
      if (!dragging) return;
      dragging = false;
      this.dom.deckPanel?.classList.remove('dragging');
      this.dom.drawerHandle?.classList.remove('dragging');
      const panelHeight = this.dom.deckPanel?.offsetHeight || 0;
      const closeThreshold = Math.min(140, panelHeight * 0.3);
      const shouldClose = dragDeltaY > closeThreshold;
      this.dom.deckPanel?.style.removeProperty('--drag-translate');
      dragDeltaY = 0;
      if (shouldClose) this.setDrawer(false);
    };

    this.dom.drawerHandle?.addEventListener('touchstart', onDragStart, { passive: true });
    this.dom.drawerHandle?.addEventListener('touchmove', onDragMove, { passive: true });
    this.dom.drawerHandle?.addEventListener('touchend', onDragEnd, { passive: true });
    this.dom.drawerHandle?.addEventListener('touchcancel', onDragEnd, { passive: true });

    // Escape Key Handler
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        e.stopImmediatePropagation?.();
        e.stopPropagation?.();
        e.preventDefault?.();
        let popoverClosed = false;
        if (this.dom.deckIconPopover && !this.dom.deckIconPopover.classList.contains('hidden')) {
          this.dom.deckIconPopover.classList.add('hidden');
          popoverClosed = true;
        }
        if (this.dom.deckColorPopover && !this.dom.deckColorPopover.classList.contains('hidden')) {
          this.dom.deckColorPopover.classList.add('hidden');
          popoverClosed = true;
        }
        if (popoverClosed) return;
        const editedDeckId = this.currentDeck?.id || this.lastEditedDeckId;
        this.close();
        this.game.deckBuilder?.openModal({ selectDeckKey: editedDeckId });
      }
    });

    // Last-resort autosave flush before a refresh/tab-close discards
    // anything still sitting in the debounce timer.
    window.addEventListener('beforeunload', () => this.flushAutosave());

    // Re-adjust card title sizes dynamically on window resize / orientation change
    const onDeckBuilderResize = () => {
      if (this.isOpen()) {
        try {
          window.scrollTo(0, 0);
          if (document.documentElement) document.documentElement.scrollTop = 0;
          if (document.body) document.body.scrollTop = 0;
        } catch (e) {}
        if (this.dom.cardsGrid) this._autoFitCardTitles(this.dom.cardsGrid);
        if (this.dom.deckStrips) this._autoFitCardTitles(this.dom.deckStrips);
      }
    };
    window.addEventListener('resize', onDeckBuilderResize);
    window.addEventListener('orientationchange', () => {
      onDeckBuilderResize();
      setTimeout(onDeckBuilderResize, 50);
      setTimeout(onDeckBuilderResize, 150);
      setTimeout(onDeckBuilderResize, 350);
    });
  }

  // ── Open / Close ───────────────────────────────────────────────────────
  open() {
    if (!this.modalEl) return;
    this.game?.pauseForMenu?.('deckBuilder');
    this.modalEl.classList.remove('hidden');
    this.modalEl.classList.add('active');

    this.cards = getActiveCards();
    this._updateSearchPlaceholder();
    this._updateSearchClearVisibility();
    this._prewarmCardThumbnails();
    this.loadDecks();
    this.render();
  }

  // Addresses the player by their profile name (falls back to "Commander").
  _updateSearchPlaceholder() {
    if (!this.dom?.searchInput) return;
    this.dom.searchInput.placeholder = `Cards or Attributes, ${getDisplayName() || 'Commander'}?`;
  }

  clearSearch() {
    if (this.dom?.searchInput) {
      this.dom.searchInput.value = '';
    }
    this.searchQuery = '';
    this._updateSearchClearVisibility();
    this.renderCollection();
  }

  _updateSearchClearVisibility() {
    const hasVal = Boolean(this.dom?.searchInput?.value);
    if (this.dom?.searchWrapper) {
      this.dom.searchWrapper.classList.toggle('has-value', hasVal);
    }
    if (this.dom?.searchClearBtn) {
      this.dom.searchClearBtn.style.display = hasVal ? 'inline-flex' : 'none';
    }
  }

  _prewarmCardThumbnails() {
    if (typeof Image === 'undefined') return;
    preloadAllCardThumbnails(this.cards || getActiveCards());
    if (!this._thumbPreloadCache) {
      this._thumbPreloadCache = PRELOADED_CARD_THUMBS;
    }
    const cards = this.cards || getActiveCards();
    if (!Array.isArray(cards)) return;
    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      const src = getCardThumbUrl(card.images && card.images.length > 0 ? card.images[0] : 'Marine_1.png');
      if (src && !this._thumbPreloadCache.has(src)) {
        const img = new Image();
        img.loading = 'eager';
        img.decoding = 'sync';
        img.src = src;
        if (typeof img.decode === 'function') {
          img.decode().catch(() => {});
        }
        this._thumbPreloadCache.set(src, img);
      }
    }
  }

  close() {
    if (!this.modalEl) return;
    const wasOpen = this.isOpen();
    if (this.currentDeck?.id) {
      this.lastEditedDeckId = this.currentDeck.id;
    }
    this.flushAutosave();
    if (this.dom.deckIconPopover) {
      this.dom.deckIconPopover.classList.add('hidden');
    }
    if (this.dom.deckColorPopover) {
      this.dom.deckColorPopover.classList.add('hidden');
    }
    this.modalEl.classList.remove('active');
    this.modalEl.classList.add('hidden');
    this.setDrawer(false);
    // Release the prewarmed decodes (~1.1 MB each x every active card, ~47 MB
    // total): they only help the next open, and the iOS PWA must not carry
    // them through a match (jetsam history, see ios-white-flash-crash notes).
    // The HTTP cache keeps the files, so re-opening only re-decodes.
    this._thumbPreloadCache = null;
    if (wasOpen) {
      this.game?.resumeForMenu?.('deckBuilder');
    }
  }

  isOpen() {
    return this.modalEl && this.modalEl.classList.contains('active');
  }

  isDesktop() {
    if (typeof window === 'undefined') return true;
    if (window.matchMedia) {
      const isTouch = window.matchMedia('(pointer: coarse)').matches;
      const isNarrowPhone = window.matchMedia('(max-width: 768px)').matches;
      const isLandscapePhone = window.matchMedia('(orientation: landscape) and (max-height: 560px)').matches ||
                               window.matchMedia('(orientation: landscape) and (max-height: 500px)').matches;
      if (isTouch || isNarrowPhone || isLandscapePhone) return false;
      return true;
    }
    return true;
  }

  setDrawer(open) {
    this.dom.deckPanel?.classList.toggle('drawer-open', open);
    this.dom.drawerBackdrop?.classList.toggle('active', open);
  }

  openDeck(deckId) {
    const found = this.savedDecks.find(d => d.id === deckId);
    if (found) {
      this.flushAutosave();
      this.currentDeck = JSON.parse(JSON.stringify(found));
      this.lastEditedDeckId = deckId;
      this.audio.playClick();
      this.render();
    }
  }

  loadDecks() {
    // No auto-seeded starter deck: a deck only ever exists once the player
    // actually creates and saves one via "+ New Deck" - previously this
    // silently created and persisted a "Strike Fleet Alpha" deck the very
    // first time the modal opened, before the player asked for anything
    // (chat request "das verhindern").
    const rawCustom = loadCustomDecks();
    this.savedDecks = (rawCustom || []).map(d => this.normalizeDeck(d));
    this.currentDeck = this.savedDecks[0] || null;
  }

  normalizeDeck(deck) {
    const cardsMap = {};
    if (deck.cards && typeof deck.cards === 'object') {
      for (const [name, qty] of Object.entries(deck.cards)) {
        const card = this.cards?.find(c => c.name.toLowerCase() === name.toLowerCase()) || CARDS_DATA.find(c => c.name.toLowerCase() === name.toLowerCase());
        const maxCopies = getMaxCopiesForCard(card);
        const count = Math.min(maxCopies, Math.max(0, Number(qty) || 0));
        if (count > 0) cardsMap[name] = count;
      }
    } else {
      const allNames = [...(deck.common || []), ...(deck.uncommon || []), ...(deck.epic || [])];
      for (const name of allNames) {
        cardsMap[name] = 1;
      }
    }

    const common = [];
    const uncommon = [];
    const epic = [];
    for (const [cardName, count] of Object.entries(cardsMap)) {
      const cardObj = this.cards.find(c => c.name.toLowerCase() === cardName.toLowerCase());
      const tier = cardObj?.tier || 1;
      for (let i = 0; i < count; i++) {
        if (tier === 1) common.push(cardName);
        else if (tier === 2) uncommon.push(cardName);
        else epic.push(cardName);
      }
    }

    const derivedRace = deriveDominantRace([...common, ...uncommon, ...epic], CARDS_DATA);
    const hasHumanModule = Object.keys(cardsMap).some(name => {
      const c = this.cards.find(card => card.name.toLowerCase() === name.toLowerCase());
      return c && (c.type === 'Module' || c.isModule) && (!c.race || c.race === 'Human');
    });
    const race = hasHumanModule ? 'Human' : (deck.race || derivedRace);
    return {
      id: deck.id || makeCustomDeckId(race),
      name: deck.name || 'Custom Deck',
      icon: deck.icon || inventory.getEquippedDeckIcon?.() || 'generic-a',
      color: deck.color || null,
      cards: cardsMap,
      common,
      uncommon,
      epic,
      race
    };
  }

  persistDecks() {
    saveCustomDecks(this.savedDecks);
    this.game.deckBuilder?.renderDecksList();
  }

  async createNewDeck() {
    this.flushAutosave();
    if (this.savedDecks.length >= MAX_CUSTOM_DECKS) {
      await this.showDialog({
        title: 'Deck Limit Reached',
        message: `Maximum of ${MAX_CUSTOM_DECKS} custom decks reached. Delete an existing deck first.`,
        mode: 'alert'
      });
      return;
    }
    const race = 'Human';
    const newDeck = {
      id: makeCustomDeckId(race),
      name: 'Strike Fleet ' + (this.savedDecks.length + 1),
      icon: inventory.getEquippedDeckIcon?.() || 'generic-a',
      cards: {},
      common: [],
      uncommon: [],
      epic: [],
      race
    };
    this.savedDecks.push(newDeck);
    this.currentDeck = newDeck;
    this.lastEditedDeckId = newDeck.id;
    this.persistDecks();
    this.audio.playClick();
    this.render();
  }

  render() {
    this.renderCollection();
    this.renderDeckTray();
  }

  get filterAttackTarget() {
    if (this.filterAttackGround && this.filterAttackAir) return 'both';
    if (this.filterAttackGround) return 'ground';
    if (this.filterAttackAir) return 'air';
    return 'all';
  }

  set filterAttackTarget(val) {
    if (val === 'both') {
      this.filterAttackGround = true;
      this.filterAttackAir = true;
    } else if (val === 'ground') {
      this.filterAttackGround = true;
      this.filterAttackAir = false;
    } else if (val === 'air') {
      this.filterAttackGround = false;
      this.filterAttackAir = true;
    } else {
      this.filterAttackGround = false;
      this.filterAttackAir = false;
    }
  }

  setAttackTargetFilter(target) {
    if (target === 'all') {
      this.filterAttackGround = false;
      this.filterAttackAir = false;
    } else if (target === 'ground') {
      this.filterAttackGround = !this.filterAttackGround;
    } else if (target === 'air') {
      this.filterAttackAir = !this.filterAttackAir;
    }
    this.updateAttackTargetButtons();
    this.renderCollection();
  }

  updateAttackTargetButtons() {
    if (!this.dom?.attackTargetBtns) return;
    const isAll = !this.filterAttackGround && !this.filterAttackAir;
    this.dom.attackTargetBtns.forEach(btn => {
      const target = btn.dataset.attackTarget;
      if (target === 'all') {
        btn.classList.toggle('active', isAll);
      } else if (target === 'ground') {
        btn.classList.toggle('active', this.filterAttackGround);
      } else if (target === 'air') {
        btn.classList.toggle('active', this.filterAttackAir);
      }
    });
  }

  setRarityFilter(rarity) {
    this.filterRarity = rarity || 'all';
    if (this.dom?.rarityBtns) {
      this.dom.rarityBtns.forEach(btn => {
        const r = btn.dataset.rarity;
        btn.classList.toggle('active', r.toLowerCase() === this.filterRarity.toLowerCase());
      });
    }
    this.renderCollection();
  }

  // ── Filter Logic ─────────────────────────────────────────────────────────
  getFilteredCards() {
    return this.cards.filter(c => {
      // Faction
      if (this.filterFaction !== 'all' && c.race !== this.filterFaction) return false;

      // Rarity
      if (this.filterRarity !== 'all') {
        const cardRarity = c.rarity || (c.tier === 1 ? 'Common' : (c.tier === 2 ? 'Uncommon' : (c.tier === 3 ? 'Epic' : 'Common')));
        if (cardRarity.toLowerCase() !== this.filterRarity.toLowerCase()) return false;
      }

      // Tier
      if (this.filterTier !== 'all' && c.tier !== this.filterTier) return false;

      // Plasma Cost Brackets
      if (this.filterCost === 'low' && (c.cost < 5 || c.cost > 10)) return false;
      if (this.filterCost === 'mid' && (c.cost < 15 || c.cost > 20)) return false;
      if (this.filterCost === 'high' && c.cost < 30) return false;

      // Flying Filter
      if (this.filterFlying && !(c.isAir || c.elementType === 'Air')) return false;

      // Attack Target Filter (Ground / Air / Both)
      if (this.filterAttackTarget !== 'all') {
        const canAttack = (c.damage > 0 || c.isKamikaze) && !c.isSpell && c.canAttack !== false;
        if (!canAttack) return false;

        const hitsGround = c.targetsGround !== false;
        const hitsAir = c.targetsAir !== false;

        if (this.filterAttackTarget === 'ground' && !hitsGround) return false;
        if (this.filterAttackTarget === 'air' && !hitsAir) return false;
        if (this.filterAttackTarget === 'both' && !(hitsGround && hitsAir)) return false;
      }

      // Search Query & Type/Keyword Search
      if (this.searchQuery && !matchesCardSearch(c, this.searchQuery)) {
        return false;
      }

      return true;
    }).sort((a, b) => a.cost - b.cost || a.name.localeCompare(b.name));
  }

  // ── Render Collection Cards Grid ─────────────────────────────────────────
  renderCollection(forceRebuild = false) {
    const grid = this.dom?.cardsGrid;
    if (!grid) return;

    const list = this.getFilteredCards();
    if (list.length === 0) {
      grid.innerHTML = `
        <div style="grid-column: 1/-1; text-align: center; padding: 48px 20px; color: var(--text-muted);">
          <div style="margin-bottom: 8px;">${getIconSvg('search', 28, '', 'opacity: 0.6;')}</div>
          <div style="font-family: var(--font-display); font-size: 15px; color: #fff;">No matching cards</div>
          <div style="font-size: 12px; margin-top: 4px;">Try clearing filters or search query.</div>
        </div>
      `;
      return;
    }

    const theme = inventory?.getEquippedDeckbuilderTheme?.() || 'glass';
    const isGlass = theme === 'glass' || theme === 'fluted' || theme === 'holo';
    const isArtwork = theme === 'artwork';
    const cutClass = theme === 'holo' ? 'cut-topography' : (theme === 'fluted' ? 'cut-micro-fluted' : 'cut-knurled');
    const matClass = theme === 'holo' ? 'mat-holo' : 'mat-crystal';
    const expectedThemeClass = isGlass ? 'theme-glass' : (isArtwork ? 'theme-artwork' : 'theme-classic');

    // Fast in-place update if grid already contains the exact card list in the matching theme
    const existingCards = grid.querySelectorAll('.card-item[data-card-name]');
    if (!forceRebuild && existingCards.length === list.length && grid.querySelector('.empty-deck-hint') === null) {
      let matching = true;
      for (let i = 0; i < list.length; i++) {
        if (existingCards[i]?.dataset?.cardName !== list[i].name || !existingCards[i]?.classList?.contains(expectedThemeClass) || (isGlass && (!existingCards[i]?.classList?.contains(cutClass) || !existingCards[i]?.classList?.contains(matClass)))) {
          matching = false;
          break;
        }
      }
      if (matching) {
        list.forEach(c => this.updateCardInCollection(c.name));
        return;
      }
    }

    grid.innerHTML = '';
    if (isGlass) this._initGlassPointerTracking();

    const frag = document.createDocumentFragment();

    list.forEach((card, idx) => {
      const maxAllowed = getMaxCopiesForCard(card);
      const inDeck = this.currentDeck?.cards?.[card.name] || 0;
      const isMax = inDeck >= maxAllowed;
      const isSingleton = maxAllowed === 1;
      const img = getCardThumbUrl(card.images && card.images.length > 0 ? card.images[0] : 'Marine_1.png');

      const rarity = card.rarity || (card.tier === 1 ? 'Common' : (card.tier === 2 ? 'Uncommon' : (card.tier === 3 ? 'Epic' : 'Common')));
      const rarityClass = `rarity-${rarity.toLowerCase()}`;
      const copiesClass = inDeck > 0 ? `copies-${inDeck}` : 'copies-0';
      const themeClasses = isGlass
        ? `theme-glass glass-chassis glass-active style-crystal ${cutClass} frame-carbon ${matClass} shape-classic appeal-arcade ind-pips-top pal-overdrive ${inDeck > 0 ? `copies-${inDeck}-active` : 'copies-0-active'}`
        : (isArtwork
          ? `theme-artwork pal-overdrive ${inDeck > 0 ? `copies-${inDeck}-active` : 'copies-0-active'}`
          : 'theme-classic');

      const hyperCard = (!isSingleton && card.race !== 'Sul' && !card.isSpell && !card.isStructure)
        ? (CARDS_DATA.find(c => (c.type === 'Melt' || c.isMelt) && c.name?.toLowerCase() === `hyper ${card.name.toLowerCase()}`) || null)
        : null;
      const hasHyper = !!hyperCard;
      const hyperTitle = hasHyper ? `Melt Fusion: 3x ${card.name} → ${hyperCard.name}` : '';

      const cardEl = document.createElement('div');
      cardEl.className = `card-item ${themeClasses} ${rarityClass} ${hasHyper ? 'has-hyper' : ''} ${inDeck > 0 ? 'in-deck' : ''} ${isMax ? 'max-in-deck' : ''} ${copiesClass}`.trim();
      cardEl.dataset.rarity = rarity;
      cardEl.dataset.copies = inDeck;
      cardEl.dataset.cardName = card.name;
      cardEl.dataset.maxCopies = maxAllowed;

      const hasStats = (card.hp && card.hp > 0) || (card.damage && card.damage > 0);

      // Attack calculation (with multi-shot and kamikaze support)
      let attackHtml = '';
      if (card.damage) {
        const attacks = card.attackCount || 1;
        const totalDamage = card.damage * attacks;
        if (card.isKamikaze) {
          attackHtml = `<span class="card-footer-stat dmg kamikaze" title="Single-use blast damage: ${totalDamage}">${getIconSvg('kamikaze', 12)} ${totalDamage}</span>`;
        } else {
          const dps = totalDamage * (card.accuracy ?? 1) / (card.attackSpeed || 1);
          const dpsStr = Number.isInteger(dps) ? dps : dps.toFixed(1).replace(/\.0$/, '');
          const targetSuffix = (card.targetsAir !== false && card.targetsGround !== false)
            ? ' • Targets: Ground & Air'
            : (card.targetsAir !== false ? ' • Targets: Air' : ' • Targets: Ground');
          attackHtml = `<span class="card-footer-stat dmg" title="Damage per second (${totalDamage} dmg / ${card.attackSpeed}s)${targetSuffix}">${getIconSvg('attack', 12)} ${dpsStr}</span>`;
        }
      }

      // Targeting badge calculation (attacker units: Circle badge)
      const isAttacker = (card.damage > 0 || card.isKamikaze) && !card.isSpell && card.canAttack !== false;
      let targetingHtml = '';
      if (isAttacker) {
        const hitsAir = card.targetsAir !== false;
        const hitsGround = card.targetsGround !== false;
        if (hitsAir && hitsGround) {
          targetingHtml = `<span class="card-badge-target target-both" title="Targets: Ground & Air">${getIconSvg('target_both', 12)}</span>`;
        } else if (hitsAir) {
          targetingHtml = `<span class="card-badge-target target-air" title="Targets: Air">${getIconSvg('target_air', 12)}</span>`;
        } else if (hitsGround) {
          targetingHtml = `<span class="card-badge-target target-ground" title="Targets: Ground">${getIconSvg('target_ground', 12)}</span>`;
        }
      }

      // Vision / Stealth badge calculation (Diamond badge)
      let visionHtml = '';
      if (card.isCloaked) {
        visionHtml = `<span class="card-badge-vision vision-stealth" title="Cloaked: Invisible without detection">${getIconSvg('stealth_eye', 11)}</span>`;
      } else if (card.isDetector || card.detectsCloak || card.globalDetector) {
        visionHtml = `<span class="card-badge-vision vision-detector" title="Detector: Reveals cloaked units">${getIconSvg('radar', 11)}</span>`;
      }

      const isAirUnit = !!(card.isAir || card.elementType === 'Air');
      const flyingHtml = isAirUnit ? `<span class="card-header-flying" title="Flying Unit">${WING_ICON_SVG}</span>` : '';
      const titleStyle = this._getCardTitleStyle(card.name);
      const descStyle = this._getCardDescStyle(card);
      const loreStyle = this._getCardLoreStyle(card);
      const fallbackSrc = getCardThumbUrl(card.images && card.images.length > 0 ? card.images[0] : 'Marine_1.png', 's');

      const pipsHtml = isSingleton
        ? `<div class="copies-pips-cluster pips-top-center single-pip-cluster" aria-hidden="true">
             <span class="pip pip-1" data-pip="1"></span>
           </div>`
        : `<div class="copies-pips-cluster pips-top-center ${hasHyper ? 'has-hyper' : ''}" ${hasHyper ? `title="${hyperTitle}"` : 'aria-hidden="true"'}>
             <span class="pip pip-1" data-pip="1"></span>
             <span class="pip pip-2" data-pip="2"></span>
             <span class="pip pip-3" data-pip="3"></span>
           </div>`;

      const priorityAttr = idx < 12 ? 'fetchpriority="high"' : '';
      const innerContent = `
        <img class="card-bg-img" src="${img}" alt="${card.name}" draggable="false" loading="eager" decoding="sync" ${priorityAttr} onerror="if (!this.dataset.fallback) { this.dataset.fallback = '1'; this.src = '${fallbackSrc}'; }" />

        <div class="card-header-bar">
          <div class="card-header-left">
            <span class="card-name" ${titleStyle}>${card.name}</span>
            ${flyingHtml}
          </div>
          <span class="card-cost-badge">${card.costDisplay || card.cost}</span>
        </div>

        ${pipsHtml}

        <div class="card-body-scrim">
          ${isGlass ? '<div class="scrim-gradient-blur" aria-hidden="true"></div>' : ''}
          <p class="card-desc" style="${descStyle}">${card.description || ''}</p>
          ${card.lore ? `<p class="card-lore" style="${loreStyle}">${card.lore}</p>` : ''}
        </div>

        <div class="card-footer-bar">
          <div class="card-footer-stats">
            ${card.hp ? `<span class="card-footer-stat hp" title="Health">${getIconSvg('hp', 12)} ${card.hp}</span>` : ''}
            ${attackHtml}
            ${!hasStats && card.isSpell ? `<span class="card-footer-stat spell">${getIconSvg('spell', 12)} Spell</span>` : ''}
            ${!hasStats && card.isStructure ? `<span class="card-footer-stat structure">${getIconSvg('structure', 12)} Structure</span>` : ''}
          </div>

          <div class="card-footer-actions">
            ${visionHtml}
            ${targetingHtml}
            <span class="card-tier-tag tier-${card.tier}" title="Tier ${card.tier} • ${rarity}">${card.tier}</span>
          </div>
        </div>
      `;

      cardEl.innerHTML = isGlass ? `
        <div class="glass-button-dome" aria-hidden="true"></div>
        <div class="glass-bezel-fiberglow" aria-hidden="true"></div>
        <div class="glass-recess-glow" aria-hidden="true"></div>
        <div class="glass-overlay-container" aria-hidden="true">
          <div class="glass-material-tint"></div>
          <div class="glass-sheen-diagonal"></div>
          <div class="glass-glare-interactive"></div>
          <div class="glass-prismatic-border"></div>
          <div class="glass-noise-grain"></div>
        </div>
        <div class="card-inner-chassis">
          ${innerContent}
        </div>
      ` : innerContent;

      cardEl.addEventListener('click', (e) => {
        const inDeckNow = this.currentDeck?.cards?.[card.name] || 0;
        const maxCopies = getMaxCopiesForCard(card);
        if (inDeckNow >= maxCopies) {
          this._triggerCardJolt(cardEl, 'remove');
          this.removeAllCopies(card.name);
        } else {
          this._triggerCardJolt(cardEl, 'add');
          this.addCard(card.name, e);
        }
      });

      frag.appendChild(cardEl);
    });

    grid.appendChild(frag);

    this._autoFitCardTitles(grid);
  }

  // Tactile micro-jolt animation on card press (Single vertical snap-in on Add, Reject-Shake on 3->0 Reset)
  _triggerCardJolt(cardEl, type = 'add') {
    if (!cardEl) return;
    if (cardEl._joltTimer) {
      clearTimeout(cardEl._joltTimer);
      cardEl._joltTimer = null;
    }
    if (cardEl._joltOnEnd) {
      cardEl.removeEventListener('animationend', cardEl._joltOnEnd);
      cardEl.removeEventListener('webkitAnimationEnd', cardEl._joltOnEnd);
      cardEl._joltOnEnd = null;
    }

    // Modern high-performance path: Web Animations API runs on compositor
    // without triggering forced synchronous reflow (zero layout stall).
    if (typeof cardEl.animate === 'function') {
      const isRemove = type === 'remove';
      const keyframes = isRemove
        ? [
            { transform: 'translate3d(0, 0, 0) scale(1) rotate(0deg)' },
            { transform: 'translate3d(-4px, 1px, 0) scale(0.98) rotate(-2deg)', offset: 0.2 },
            { transform: 'translate3d(4px, 1px, 0) scale(0.98) rotate(2deg)', offset: 0.45 },
            { transform: 'translate3d(-2.5px, 0, 0) scale(0.99) rotate(-1deg)', offset: 0.7 },
            { transform: 'translate3d(0, 0, 0) scale(1) rotate(0deg)' }
          ]
        : [
            { transform: 'translate3d(0, 0, 0) scale(1)' },
            { transform: 'translate3d(0, 3px, 0) scale(0.976)', offset: 0.38 },
            { transform: 'translate3d(0, 0, 0) scale(1)' }
          ];
      const duration = isRemove ? 240 : 150;
      const easing = isRemove ? 'cubic-bezier(0.36, 0.07, 0.19, 0.97)' : 'cubic-bezier(0.22, 1, 0.36, 1)';
      if (cardEl._activeJoltAnim) {
        try { cardEl._activeJoltAnim.cancel(); } catch {}
      }
      cardEl._activeJoltAnim = cardEl.animate(keyframes, { duration, easing });
      return;
    }

    const cls = type === 'remove' ? 'jolt-remove' : 'jolt-add';
    cardEl.classList.remove('jolt-add', 'jolt-remove');
    // Clear animation property directly so WebKit style engine restarts cleanly
    cardEl.style.animation = 'none';
    cardEl.style.webkitAnimation = 'none';
    void cardEl.offsetHeight;
    cardEl.style.animation = '';
    cardEl.style.webkitAnimation = '';
    cardEl.classList.add(cls);

    const onEnd = (e) => {
      if (e.target !== cardEl) return;
      if (cardEl._joltTimer) {
        clearTimeout(cardEl._joltTimer);
        cardEl._joltTimer = null;
      }
      cardEl.classList.remove('jolt-add', 'jolt-remove');
      cardEl.removeEventListener('animationend', onEnd);
      cardEl.removeEventListener('webkitAnimationEnd', onEnd);
      cardEl._joltOnEnd = null;
    };
    cardEl._joltOnEnd = onEnd;
    cardEl.addEventListener('animationend', onEnd);
    cardEl.addEventListener('webkitAnimationEnd', onEnd);

    // Safety fallback cleanup
    cardEl._joltTimer = setTimeout(() => {
      cardEl._joltTimer = null;
      cardEl.classList.remove('jolt-add', 'jolt-remove');
      cardEl.removeEventListener('animationend', onEnd);
      cardEl.removeEventListener('webkitAnimationEnd', onEnd);
      cardEl._joltOnEnd = null;
    }, 320);
  }

  // Subtle interactive pointer-glare reflection over the crystal front glass (rAF-throttled & mouse-only)
  _initGlassPointerTracking() {
    if (this._glassPointerBound) return;
    this._glassPointerBound = true;
    const grid = this.dom?.cardsGrid;
    if (!grid) return;

    let activeCard = null;
    let cardRect = null;
    let rafPending = false;
    let targetX = 50;
    let targetY = 30;

    grid.addEventListener('pointerenter', (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      const card = e.target.closest?.('.card-item.theme-glass');
      if (card) {
        activeCard = card;
        cardRect = card.getBoundingClientRect();
      }
    }, { capture: true, passive: true });

    grid.addEventListener('pointerleave', (e) => {
      if (activeCard && (e.target === activeCard || !activeCard.contains(e.relatedTarget))) {
        activeCard = null;
        cardRect = null;
      }
    }, { capture: true, passive: true });

    grid.addEventListener('pointermove', (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      const card = e.target.closest?.('.card-item.theme-glass');
      if (!card) {
        activeCard = null;
        cardRect = null;
        return;
      }
      if (card !== activeCard || !cardRect) {
        activeCard = card;
        cardRect = card.getBoundingClientRect();
      }
      const normX = Math.max(0, Math.min(1, (e.clientX - cardRect.left) / cardRect.width));
      const normY = Math.max(0, Math.min(1, (e.clientY - cardRect.top) / cardRect.height));
      targetX = (normX * 100).toFixed(1);
      targetY = (normY * 100).toFixed(1);

      if (!rafPending) {
        rafPending = true;
        requestAnimationFrame(() => {
          rafPending = false;
          if (activeCard) {
            activeCard.style.setProperty('--mouse-x', `${targetX}%`);
            activeCard.style.setProperty('--mouse-y', `${targetY}%`);
          }
        });
      }
    }, { passive: true });
  }

  // In-place DOM update for a single card in the collection grid (zero reflow/rebuild)
  updateCardInCollection(cardName) {
    const grid = this.dom?.cardsGrid;
    if (!grid) return;
    const cardEl = grid.querySelector(`[data-card-name="${CSS.escape(cardName)}"]`);
    if (!cardEl) return;

    const card = this.cards?.find(c => c.name.toLowerCase() === cardName.toLowerCase()) || CARDS_DATA.find(c => c.name.toLowerCase() === cardName.toLowerCase());
    const maxCopies = getMaxCopiesForCard(card);
    const inDeck = this.currentDeck?.cards?.[cardName] || 0;
    const isMax = inDeck >= maxCopies;
    const isGlass = cardEl.classList.contains('theme-glass');
    const isArtwork = cardEl.classList.contains('theme-artwork');

    cardEl.dataset.copies = inDeck;
    cardEl.dataset.maxCopies = maxCopies;
    cardEl.classList.remove(
      'in-deck', 'max-in-deck',
      'copies-0', 'copies-1', 'copies-2', 'copies-3',
      'copies-0-active', 'copies-1-active', 'copies-2-active', 'copies-3-active'
    );
    if (inDeck > 0) {
      cardEl.classList.add('in-deck', `copies-${inDeck}`);
      if (isGlass || isArtwork) cardEl.classList.add(`copies-${inDeck}-active`);
    } else {
      cardEl.classList.add('copies-0');
      if (isGlass || isArtwork) cardEl.classList.add('copies-0-active');
    }
    if (isMax) {
      cardEl.classList.add('max-in-deck');
    }

    const qtyTag = cardEl.querySelector('.card-qty-tag');
    if (qtyTag) {
      qtyTag.className = `card-qty-tag ${inDeck > 0 ? `has-cards copies-${inDeck}` : ''}`;
      qtyTag.textContent = inDeck > 0 ? inDeck : '+';
    }
  }

  // ── Render Deck Tray ──────────────────────────────────────────────────────
  // Single view: whichever deck was opened (via the deck-selection screen's
  // pencil icon or "+ New Deck"). There is no more internal "My Decks" list
  // to browse inside the builder itself - deleteCurrentDeck() always exits
  // back to that top-level deck-selection screen instead.
  renderDeckTray() {
    if (this.dom.deckViewEdit) this.dom.deckViewEdit.style.display = 'flex';

    const stats = this.getDeckStats();

    if (this.dom.deckInput) this.dom.deckInput.value = this.currentDeck?.name || '';
    if (this.dom.deckCountBadge) {
      this.dom.deckCountBadge.textContent = `${stats.total} Cards`;
      const isComplete = (stats.total || 0) >= MIN_DECK_SIZE;
      this.dom.deckCountBadge.classList.toggle('is-complete', isComplete);
      this.dom.deckCountBadge.classList.toggle('is-incomplete', !isComplete);
    }
    this.renderValidityHint(stats);
    this.updateDeckIconPreview();
    this.updateDeckColorPreview();

    // Plasma Curve
    this.renderPlasmaCurve(stats);

    // Card Strips
    this.renderCardStrips();

    // Mobile Bottom Bar
    this.updateMobileBarEdit(stats);
  }

  updateDeckIconPreview() {
    const previewEl = this.dom.deckIconPreview;
    if (!previewEl || !this.currentDeck) return;
    const iconId = this.currentDeck.icon || inventory.getEquippedDeckIcon?.() || 'generic-a';
    previewEl.innerHTML = getFactionCrestMarkup(iconId, '-edit-preview');
    previewEl.className = 'deck-icon-preview has-crest';
  }

  toggleIconPopover() {
    const popover = this.dom.deckIconPopover;
    if (!popover) return;
    const isHidden = popover.classList.toggle('hidden');
    if (!isHidden) {
      if (this.dom.deckColorPopover) this.dom.deckColorPopover.classList.add('hidden');
      this.renderIconPopover();
    }
  }

  renderIconPopover() {
    const popover = this.dom.deckIconPopover;
    if (!popover || !this.currentDeck) return;
    const currentIcon = this.currentDeck.icon || inventory.getEquippedDeckIcon?.() || 'generic-a';
    popover.innerHTML = `
      <div class="deck-icon-popover-title">Deck Icon</div>
      <div class="deck-icon-grid">
        ${DECK_ICON_PRESETS.map((p, idx) => {
          const isSelected = p.id === currentIcon;
          const isUnlocked = inventory.hasDeckIcon(p.id);
          let content = '';
          if (p.isCrest) {
            content = `<div class="popover-crest-wrap">${getFactionCrestMarkup(p.id, `-popover-${idx}`)}</div>`;
          } else {
            content = `<span class="popover-glyph">${p.glyph}</span>`;
          }
          return `
            <button class="deck-icon-option ${isSelected ? 'active' : ''} ${!isUnlocked ? 'locked' : ''}"
                    data-icon="${p.id}"
                    data-unlocked="${isUnlocked}"
                    type="button"
                    title="${p.label}${!isUnlocked ? ' (Gewinnbar in Supply Surge)' : ''}">
              ${content}
              ${!isUnlocked ? `<span class="deck-icon-lock-badge">${getIconSvg('lock', 11)}</span>` : ''}
            </button>
          `;
        }).join('')}
      </div>
    `;

    popover.querySelectorAll('.deck-icon-option').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (btn.dataset.unlocked === 'false') {
          if (this.audio) this.audio.playClick?.();
          return;
        }
        this.audio.playClick();
        const iconId = btn.dataset.icon;
        if (this.currentDeck) {
          this.currentDeck.icon = iconId;
          this.updateDeckIconPreview();
          this.autosaveDeck();
          this.game.deckBuilder?.renderDecksList?.();
        }
        popover.classList.add('hidden');
      });
    });
  }

  updateDeckColorPreview() {
    const previewEl = this.dom.deckColorPreview;
    if (!previewEl || !this.currentDeck) return;
    const customColorId = this.currentDeck.color;
    const style = customColorId ? REEL_3_STYLES_MAP.get(customColorId) : null;
    const globalDefaultId = inventory.getEquippedDeckColor();
    const globalStyle = (!style && globalDefaultId) ? REEL_3_STYLES_MAP.get(globalDefaultId) : null;
    const activeStyle = style || globalStyle;

    if (activeStyle) {
      previewEl.className = 'deck-color-preview';
      previewEl.style.backgroundColor = activeStyle.hex;
      previewEl.style.borderColor = activeStyle.hex;
      previewEl.style.boxShadow = `0 0 10px ${activeStyle.hex}aa`;
    } else {
      const isSul = this.currentDeck.race === 'Sul';
      previewEl.className = `deck-color-preview ${isSul ? 'default-sul' : 'default-human'}`;
      previewEl.style.backgroundColor = '';
      previewEl.style.borderColor = '';
      previewEl.style.boxShadow = '';
    }
  }

  toggleColorPopover() {
    const popover = this.dom.deckColorPopover;
    if (!popover) return;
    const isHidden = popover.classList.toggle('hidden');
    if (!isHidden) {
      if (this.dom.deckIconPopover) this.dom.deckIconPopover.classList.add('hidden');
      this.renderColorPopover();
    }
  }

  renderColorPopover() {
    const popover = this.dom.deckColorPopover;
    if (!popover || !this.currentDeck) return;
    const currentColor = this.currentDeck.color || null;
    const isSul = this.currentDeck.race === 'Sul';
    const defaultColorHex = isSul ? '#f472b6' : '#00f0ff';
    const isDefaultSelected = !currentColor;

    popover.innerHTML = `
      <div class="deck-color-popover-title">Deck Color Theme</div>
      <div class="deck-color-grid">
        <button class="deck-color-option ${isDefaultSelected ? 'active' : ''}" data-color="default" type="button" title="Default Theme">
          <span class="deck-color-swatch-bubble" style="background: ${defaultColorHex}; box-shadow: 0 0 8px ${defaultColorHex}"></span>
          <span class="deck-color-option-name">Default</span>
        </button>
        ${REEL_3_STYLES.map(s => {
          const isUnlocked = inventory.hasDeckColor(s.id);
          const isSelected = s.id === currentColor;
          return `
            <button class="deck-color-option ${isSelected ? 'active' : ''} ${!isUnlocked ? 'locked' : ''}"
                    data-color="${s.id}"
                    data-unlocked="${isUnlocked}"
                    type="button"
                    title="${isUnlocked ? s.name : `${s.name} (Locked - Win in Slot Machine)`}">
              <span class="deck-color-swatch-bubble" style="background-color: ${s.hex}; box-shadow: 0 0 ${isUnlocked ? '8px ' + s.hex : 'none'}">
                ${!isUnlocked ? `<span class="deck-color-lock-badge">${getIconSvg('lock', 11)}</span>` : ''}
              </span>
              <span class="deck-color-option-name">${s.name}</span>
            </button>
          `;
        }).join('')}
      </div>
    `;

    popover.querySelectorAll('.deck-color-option').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const colorId = btn.dataset.color;
        const isUnlocked = btn.dataset.unlocked === 'true' || colorId === 'default';
        if (!isUnlocked) {
          if (this.audio?.playDenied) this.audio.playDenied();
          else if (this.audio?.playClick) this.audio.playClick();
          return;
        }

        if (this.audio?.playClick) this.audio.playClick();
        if (this.currentDeck) {
          this.currentDeck.color = colorId === 'default' ? null : colorId;
          this.updateDeckColorPreview();
          this.autosaveDeck();
          this.game.deckBuilder?.renderDecksList?.();
        }
        popover.classList.add('hidden');
      });
    });
  }

  renderPlasmaCurve(stats) {
    if (!this.dom.curveBars) return;
    if (this.dom.curveAvg) this.dom.curveAvg.textContent = `Avg: ${stats.avg}`;

    this.dom.curveBars.innerHTML = '';

    if (!stats || stats.total === 0 || !stats.costs || stats.costs.length === 0) {
      this.dom.curveBars.innerHTML = `
        <div class="rod-grid-ticks" aria-hidden="true">
          <span></span><span></span><span></span><span></span><span></span><span></span><span></span><span></span>
        </div>
        <div class="rod-empty-hint">DORMANT FUEL ROD</div>
      `;
      return;
    }

    const MAX_SCALE = 70;
    const costToPct = (val) => {
      const clamped = Math.max(0, Math.min(MAX_SCALE, val));
      return 4 + (clamped / MAX_SCALE) * 92;
    };

    const minPct = costToPct(stats.min);
    const q1Pct = costToPct(stats.q1);
    const medPct = costToPct(stats.median);
    const q3Pct = costToPct(stats.q3);
    const maxPct = costToPct(stats.max);
    const whiskerWidth = Math.max(1, maxPct - minPct);
    const coreWidth = Math.max(2, q3Pct - q1Pct);
    const hasSpread = stats.min !== stats.max;

    this.dom.curveBars.innerHTML = `
      <div class="rod-grid-ticks" aria-hidden="true">
        <span></span><span></span><span></span><span></span><span></span><span></span><span></span><span></span>
      </div>
      <div class="rod-whisker" style="left: ${minPct}%; width: ${whiskerWidth}%;"></div>
      <div class="rod-core" style="left: ${q1Pct}%; width: ${coreWidth}%;" title="Core: ${stats.q1}-${stats.q3} Plasma (50% of Deck)"></div>
      <div class="rod-median" style="left: ${medPct}%;" title="Median: ${stats.median} Plasma">
        <div class="rod-median-pin-top"></div>
        <div class="rod-median-diamond"></div>
        <div class="rod-median-pin-bottom"></div>
      </div>
      ${hasSpread ? `
        <div class="rod-badge rod-min" style="left: ${minPct}%;" title="Min: ${stats.min} Plasma">${stats.min}</div>
        <div class="rod-badge rod-max" style="left: ${maxPct}%;" title="Max: ${stats.max} Plasma">${stats.max}</div>
      ` : `
        <div class="rod-badge rod-min" style="left: ${minPct}%;" title="Cost: ${stats.min} Plasma">${stats.min}</div>
      `}
    `;
  }

  renderCardStrips(forceRebuild = false) {
    const list = this.dom.deckStrips;
    if (!list) return;

    const entries = Object.entries(this.currentDeck?.cards || {});
    if (entries.length === 0) {
      list.innerHTML = `
        <div class="empty-deck-hint">
          <div class="empty-deck-icon-wrap" aria-hidden="true">
            <svg class="empty-deck-svg" viewBox="0 0 48 48" width="40" height="40" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
              <rect x="15" y="6" width="22" height="31" rx="3.5" class="empty-card-back" />
              <rect x="10" y="11" width="22" height="31" rx="3.5" class="empty-card-front" />
              <line x1="21" y1="22.5" x2="21" y2="30.5" class="empty-card-plus" stroke-width="2" />
              <line x1="17" y1="26.5" x2="25" y2="26.5" class="empty-card-plus" stroke-width="2" />
            </svg>
          </div>
          <div style="font-family: var(--font-display); font-size: 13.5px; color: #fff;">Deck is empty</div>
          <div class="empty-deck-hint-desktop" style="font-size: 11.5px;">Click cards on the left to add copies.</div>
          <button type="button" class="empty-deck-hint-mobile-cta">↩ Tap to browse cards</button>
        </div>
      `;
      list.querySelector('.empty-deck-hint-mobile-cta')?.addEventListener('click', () => this.setDrawer(false));
      return;
    }

    // Sort by Cost, then Name
    const sorted = entries.map(([name, qty]) => {
      const card = this.cards.find(c => c.name.toLowerCase() === name.toLowerCase()) || { name, cost: 0, tier: 1 };
      return { card, qty };
    }).sort((a, b) => a.card.cost - b.card.cost || a.card.name.localeCompare(b.card.name));

    // Fast in-place update if existing strips match sorted card keys
    const existingStrips = list.querySelectorAll('.card-strip[data-card-name]');
    if (!forceRebuild && existingStrips.length === sorted.length && list.querySelector('.empty-deck-hint') === null) {
      let matching = true;
      for (let i = 0; i < sorted.length; i++) {
        if (existingStrips[i].dataset.cardName !== sorted[i].card.name) {
          matching = false;
          break;
        }
      }
      if (matching) {
        for (let i = 0; i < sorted.length; i++) {
          const { card, qty } = sorted[i];
          const strip = existingStrips[i];
          strip.title = qty > 1
            ? `Click to remove 1 copy of ${card.name} (${qty - 1} left)`
            : `Click to remove ${card.name} from deck`;
          let countLabel = strip.querySelector('.strip-count-label');
          if (qty >= 1) {
            if (countLabel) {
              countLabel.textContent = qty;
              countLabel.className = `strip-count-label copies-${qty}`;
            } else {
              countLabel = document.createElement('span');
              countLabel.className = `strip-count-label copies-${qty}`;
              countLabel.textContent = qty;
              const qtyBadge = strip.querySelector('.strip-qty');
              if (qtyBadge) {
                strip.insertBefore(countLabel, qtyBadge);
              } else {
                strip.appendChild(countLabel);
              }
            }
          } else if (countLabel) {
            countLabel.remove();
          }
        }
        return;
      }
    }

    list.innerHTML = '';

    sorted.forEach(({ card, qty }) => {
      const img = getCardThumbUrl(card.images && card.images.length > 0 ? card.images[0] : 'Marine_1.png');
      const rarity = card.rarity || (card.tier === 1 ? 'Common' : (card.tier === 2 ? 'Uncommon' : (card.tier === 3 ? 'Epic' : 'Common')));
      const rarityClass = `rarity-${rarity.toLowerCase()}`;
      const typeInfo = getCardTypeInfo(card);
      const strip = document.createElement('div');
      strip.className = `card-strip tier-${card.tier} ${rarityClass}`;
      strip.dataset.cardName = card.name;
      strip.title = qty > 1
        ? `Click to remove 1 copy of ${card.name} (${qty - 1} left)`
        : `Click to remove ${card.name} from deck`;
      const stripTitleStyle = this._getStripTitleStyle(card.name);

      strip.innerHTML = `
        <div class="card-strip-bg" style="background-image: url('${img}');"></div>
        <div class="card-strip-scrim"></div>
        <div class="strip-cost">${card.costDisplay || card.cost}</div>
        <div class="strip-name" ${stripTitleStyle}>${card.name}</div>
        <span class="strip-count-label copies-${qty}">${qty}</span>
        <div class="strip-qty type-${typeInfo.type}" title="${card.name} • ${typeInfo.label} (${rarity})">
          <span class="strip-type-symbol">${typeInfo.svg}</span>
        </div>
      `;

      strip.addEventListener('click', () => this.removeCard(card.name));
      list.appendChild(strip);
    });

    this._autoFitCardTitles(list);
  }

  // ── Card Operations ──────────────────────────────────────────────────────
  addCard(name, event) {
    if (!this.currentDeck) return;
    if (!this.currentDeck.cards) this.currentDeck.cards = {};

    const card = this.cards?.find(c => c.name.toLowerCase() === name.toLowerCase()) || CARDS_DATA.find(c => c.name.toLowerCase() === name.toLowerCase());
    const maxCopies = getMaxCopiesForCard(card);
    const count = this.currentDeck.cards[name] || 0;
    if (count >= maxCopies) return;

    this.currentDeck.cards[name] = Math.min(maxCopies, count + 1);
    this.audio.playAdd();
    this.updateCardInCollection(name);
    this.renderDeckTray();
    this.autosaveDeck();
  }

  // Decrement by 1 copy (or remove if only 1 copy left)
  removeCard(name) {
    if (!this.currentDeck || !this.currentDeck.cards) return;
    const count = this.currentDeck.cards[name] || 0;
    if (count <= 1) {
      delete this.currentDeck.cards[name];
    } else {
      this.currentDeck.cards[name] = count - 1;
    }
    this.audio.playRemove();
    this.updateCardInCollection(name);
    this.renderDeckTray();
    this.autosaveDeck();
  }

  // Remove all copies of this card (e.g. at the end of the 0->1->2->3->0 cycle)
  removeAllCopies(name) {
    if (!this.currentDeck || !this.currentDeck.cards) return;
    delete this.currentDeck.cards[name];
    this.audio.playRemove();
    this.updateCardInCollection(name);
    this.renderDeckTray();
    this.autosaveDeck();
  }

  getDeckStats() {
    let totalCost = 0;
    const costBuckets = { '5-10': 0, '15': 0, '20': 0, '30+': 0 };
    const costs = [];

    if (this.currentDeck && this.currentDeck.cards) {
      for (const [name, qty] of Object.entries(this.currentDeck.cards)) {
        const card = this.cards.find(c => c.name.toLowerCase() === name.toLowerCase());
        if (!card) continue;
        const cCost = card.cost || 0;
        totalCost += cCost * qty;
        for (let i = 0; i < qty; i++) {
          costs.push(cCost);
        }

        if (cCost <= 10) costBuckets['5-10'] += qty;
        else if (cCost === 15) costBuckets['15'] += qty;
        else if (cCost === 20) costBuckets['20'] += qty;
        else costBuckets['30+'] += qty;
      }
    }

    costs.sort((a, b) => a - b);
    let min = 0, max = 0, q1 = 0, median = 0, q3 = 0;
    if (costs.length > 0) {
      min = costs[0];
      max = costs[costs.length - 1];
      const n = costs.length;
      median = (n % 2 === 1) ? costs[Math.floor(n / 2)] : Math.round(((costs[n / 2 - 1] + costs[n / 2]) / 2) * 10) / 10;
      q1 = costs[Math.floor(n * 0.25)];
      q3 = costs[Math.floor(n * 0.75)];
    }

    // Rule-compliance (min tier counts) comes from the single shared
    // getDeckValidity() helper - see js/ui/deckBuilder.js's match-selection
    // gate, which checks the same thing on the autosaved-but-not-yet-legal
    // drafts autosaveDeck() below can produce.
    const { total, t1, t2, t3, isSulDeck, isValid } = getDeckValidity(
      this.currentDeck?.cards || {},
      this.cards,
      this.currentDeck?.race
    );
    const avg = total > 0 ? (totalCost / total).toFixed(1) : '0.0';

    return { total, t1, t2, t3, isValid, isSulDeck, avg, costBuckets, costs, min, max, q1, median, q3 };
  }

  // Surfaces getDeckStats()'s isValid check inline in the editor - the only
  // place left to learn "why can't I pick this deck for a match" now that
  // there's no "Save Deck" button to show it as a blocking dialog (see
  // js/ui/deckBuilder.js's renderDecksList(), which is where an invalid
  // deck actually gets kept out of match selection).
  renderValidityHint(stats) {
    const el = this.dom.deckValidityHint;
    if (!el) return;
    if (stats.isValid) {
      el.style.display = 'none';
      el.innerHTML = '';
      return;
    }

    const chipsHtml = `
      <div class="validity-chips">
        <span class="validity-chip ${stats.t1 >= 1 ? 'chip-met' : 'chip-missing'}">
          ${stats.t1 >= 1 ? '✓' : '✗'} Tier 1${stats.t1 > 0 ? ` (${stats.t1})` : ''}
        </span>
        <span class="validity-chip ${stats.t2 >= 1 ? 'chip-met' : 'chip-missing'}">
          ${stats.t2 >= 1 ? '✓' : '✗'} Tier 2${stats.t2 > 0 ? ` (${stats.t2})` : ''}
        </span>
        ${!stats.isSulDeck ? `
          <span class="validity-chip ${stats.t3 >= 1 ? 'chip-met' : 'chip-missing'}">
            ${stats.t3 >= 1 ? '✓' : '✗'} Tier 3${stats.t3 > 0 ? ` (${stats.t3})` : ''}
          </span>
        ` : ''}
        <span class="validity-chip ${stats.total >= MIN_DECK_SIZE ? 'chip-met' : 'chip-missing'}">
          ${stats.total >= MIN_DECK_SIZE ? '✓' : '✗'} ${stats.total}/${MIN_DECK_SIZE} Cards
        </span>
      </div>
    `;

    el.innerHTML = chipsHtml;
    el.style.display = 'block';
  }

  // ── Autosave ──────────────────────────────────────────────────────────
  // Persists the deck being edited a short debounce after every card
  // add/remove or rename, so leaving the editor (back button, close,
  // browser refresh) never loses a draft. IMPORTANT: this writes the
  // draft as-is, including one that doesn't meet deck requirements yet -
  // an incomplete deck must never become selectable for a match purely
  // because it now autosaves to storage. That gate lives entirely in
  // js/ui/deckBuilder.js's renderDecksList(), which runs the same
  // getDeckValidity() check before letting a saved custom deck be picked
  // to start a match; renderValidityHint() above is purely informational.
  autosaveDeck() {
    clearTimeout(this._autosaveTimer);
    this._autosaveTimer = setTimeout(() => this._persistDraft(), AUTOSAVE_DEBOUNCE_MS);
  }

  // Drops a pending autosave without writing it - used right before
  // Delete Deck removes the deck outright, where a stale debounced write
  // firing afterwards would re-add it via a captured idx === -1 lookup.
  cancelPendingAutosave() {
    clearTimeout(this._autosaveTimer);
    this._autosaveTimer = null;
  }

  // Writes a pending autosave immediately instead of waiting out the
  // debounce - called wherever the editor is about to be left (closing
  // the modal, switching back to the deck list, opening a different
  // deck, starting a new one) so no edit is ever stranded in a timer that
  // never gets to fire.
  flushAutosave() {
    if (this._autosaveTimer) {
      clearTimeout(this._autosaveTimer);
      this._autosaveTimer = null;
      this._persistDraft();
    }
  }

  _persistDraft() {
    if (!this.currentDeck) return;
    const normalized = this.normalizeDeck(this.currentDeck);
    Object.assign(this.currentDeck, normalized);

    const idx = this.savedDecks.findIndex(d => d.id === this.currentDeck.id);
    if (idx !== -1) {
      this.savedDecks[idx] = JSON.parse(JSON.stringify(this.currentDeck));
    } else {
      this.savedDecks.push(JSON.parse(JSON.stringify(this.currentDeck)));
    }
    this.persistDecks();
  }

  // ── In-UI Tactical Dialog (replaces native alert()/confirm()) ───────────
  // mode: 'alert' shows only a confirm button; 'confirm' also shows cancel.
  // Resolves true on confirm, false on cancel/dismiss.
  showDialog({ title = '', message = '', mode = 'alert', danger = false, confirmLabel = 'OK', cancelLabel = 'Cancel' } = {}) {
    const { dialogOverlay, dialogBox, dialogTitle, dialogMessage, dialogCancel, dialogConfirm } = this.dom || {};
    if (!dialogOverlay || !dialogBox || !dialogTitle || !dialogMessage || !dialogCancel || !dialogConfirm) {
      // Environment without the dialog markup (e.g. tests): fall back to
      // the native equivalent rather than silently losing the prompt.
      if (mode === 'confirm') return Promise.resolve(confirm(message));
      alert(message);
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      dialogTitle.textContent = title;
      dialogMessage.textContent = message;
      dialogBox.classList.toggle('mode-danger', !!danger);
      dialogCancel.style.display = mode === 'confirm' ? '' : 'none';
      dialogConfirm.textContent = confirmLabel;
      dialogCancel.textContent = cancelLabel;

      const cleanup = (result) => {
        dialogOverlay.classList.remove('active');
        dialogConfirm.removeEventListener('click', onConfirm);
        dialogCancel.removeEventListener('click', onCancel);
        dialogOverlay.removeEventListener('click', onBackdrop);
        resolve(result);
      };
      const onConfirm = () => { this.audio.playClick(); cleanup(true); };
      const onCancel = () => { this.audio.playClick(); cleanup(false); };
      const onBackdrop = (e) => { if (e.target === dialogOverlay) onCancel(); };

      dialogConfirm.addEventListener('click', onConfirm);
      dialogCancel.addEventListener('click', onCancel);
      dialogOverlay.addEventListener('click', onBackdrop);

      dialogOverlay.classList.add('active');
    });
  }

  // Always exits straight back to the top-level deck-selection screen (same
  // destination as the "Decks" button) - there is no more internal "My
  // Decks" list to fall back into, so deleting the last deck can no longer
  // auto-create a fresh empty one just to have something to show (chat
  // report "das sollte auch verhindern, dass automatisch ein leeres deck
  // angelegt wird").
  deleteCurrentDeck() {
    if (!this.currentDeck) return;
    const deletedId = this.currentDeck.id;
    this.cancelPendingAutosave();
    this.savedDecks = this.savedDecks.filter(d => d.id !== deletedId);
    this.persistDecks();
    this.audio.playRemove();
    this.currentDeck = null;
    this.lastEditedDeckId = null;
    try {
      if (typeof localStorage !== 'undefined' && localStorage.getItem('ucg_last_deck_id') === deletedId) {
        localStorage.removeItem('ucg_last_deck_id');
      }
    } catch (e) {}
    this.close();
    this.game.deckBuilder?.openModal();
  }

  updateMobileBarEdit(stats) {
    if (this.dom.mobileDeckLabel) {
      this.dom.mobileDeckLabel.textContent = this.currentDeck?.name || 'Active Deck';
    }
    if (this.dom.mobileDeckSub) {
      const total = stats?.total || 0;
      const isComplete = total >= MIN_DECK_SIZE;
      const countClass = isComplete ? 'count-complete' : 'count-incomplete';
      this.dom.mobileDeckSub.classList.toggle('is-complete', isComplete);
      this.dom.mobileDeckSub.classList.toggle('is-incomplete', !isComplete);
      this.dom.mobileDeckSub.innerHTML = `<span class="mobile-deck-count ${countClass}">${total} Cards</span> · Tap to edit`;
    }
    if (this.dom.mobileOpenPill) {
      this.dom.mobileOpenPill.textContent = '▲ View Deck';
    }
  }

  _getCardTitleStyle(name) {
    return getCardTitleStyle(name);
  }

  _getCardDescStyle(card) {
    return getDeckCardDescStyle(card);
  }

  _getCardLoreStyle(card) {
    return getDeckCardLoreStyle(card);
  }

  _getStripTitleStyle(name) {
    return getStripTitleStyle(name);
  }

  _autoFitCardTitles(container) {
    if (typeof window === 'undefined' || !container) return;
    const adjust = () => {
      const titleEls = container.querySelectorAll ? container.querySelectorAll('.card-name, .strip-name') : [];
      titleEls.forEach(el => {
        const isStrip = el.classList.contains('strip-name');
        // Clear prior overrides so we can measure against clean baseline style
        el.style.removeProperty('--card-title-size');
        el.style.removeProperty('--strip-title-size');
        el.style.fontSize = '';

        // If hidden, 0 width, or in the middle of layout reflow, do not shrink
        if (!el.clientWidth || el.clientWidth <= 10) return;

        const name = el.textContent || '';
        const len = name.length;
        if (isStrip) {
          if (len > 21) el.style.setProperty('--strip-title-size', '11px');
          else if (len > 18) el.style.setProperty('--strip-title-size', '12.5px');
          else if (len > 15) el.style.setProperty('--strip-title-size', '14px');
        } else {
          if (len > 21) el.style.setProperty('--card-title-size', '11px');
          else if (len > 18) el.style.setProperty('--card-title-size', '12.5px');
          else if (len > 15) el.style.setProperty('--card-title-size', '14.5px');
          else if (len > 13) el.style.setProperty('--card-title-size', '16px');
          else if (len > 11) el.style.setProperty('--card-title-size', '17.5px');
        }
      });
    };
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(adjust);
    } else {
      adjust();
    }
  }
}

export function getCardTitleStyle(name) {
  if (!name) return '';
  const len = name.length;
  if (len > 21) return 'style="--card-title-size: 11px; font-size: 11px; letter-spacing: -0.2px;"';
  if (len > 18) return 'style="--card-title-size: 12.5px; font-size: 12.5px; letter-spacing: -0.1px;"';
  if (len > 15) return 'style="--card-title-size: 14.5px; font-size: 14.5px; letter-spacing: 0.1px;"';
  if (len > 13) return 'style="--card-title-size: 16px; font-size: 16px; letter-spacing: 0.2px;"';
  if (len > 11) return 'style="--card-title-size: 17.5px; font-size: 17.5px; letter-spacing: 0.3px;"';
  return '';
}

export function getStripTitleStyle(name) {
  if (!name) return '';
  const len = name.length;
  if (len > 21) return 'style="--strip-title-size: 11px; font-size: 11px;"';
  if (len > 18) return 'style="--strip-title-size: 12.5px; font-size: 12.5px;"';
  if (len > 15) return 'style="--strip-title-size: 14px; font-size: 14px;"';
  return '';
}

export function getDeckCardDescStyle(card) {
  const desc = card?.description || '';
  const lore = card?.lore || '';
  const descLen = desc.length;
  const loreLen = lore.length;
  const total = descLen + loreLen;

  let size = 18.5;
  let lh = 1.35;
  if (total > 220) {
    size = 14.5;
    lh = 1.28;
  } else if (total > 160) {
    size = 15.5;
    lh = 1.30;
  } else if (total > 110) {
    size = 16.5;
    lh = 1.32;
  } else if (total > 60) {
    size = 17.5;
    lh = 1.34;
  }

  // Mobile landscape scaling brackets (compact 176px cards, lore hidden)
  let lsSize = 13.0;
  let lsLh = 1.25;
  if (descLen > 115) {
    lsSize = 11.0;
    lsLh = 1.18;
  } else if (descLen > 85) {
    lsSize = 11.5;
    lsLh = 1.20;
  } else if (descLen > 50) {
    lsSize = 12.2;
    lsLh = 1.22;
  }

  return `--deck-desc-size: ${size}px; font-size: ${size}px; line-height: ${lh}; --deck-desc-size-ls: ${lsSize}px; --deck-desc-lh-ls: ${lsLh};`;
}

export function getDeckCardLoreStyle(card) {
  const desc = card?.description || '';
  const lore = card?.lore || '';
  const descLen = desc.length;
  const loreLen = lore.length;
  const total = descLen + loreLen;

  if (total > 220) return '--deck-lore-size: 13.0px; font-size: 13.0px; line-height: 1.26;';
  if (total > 160) return '--deck-lore-size: 14.0px; font-size: 14.0px; line-height: 1.28;';
  if (total > 110) return '--deck-lore-size: 14.8px; font-size: 14.8px; line-height: 1.30;';
  if (total > 60) return '--deck-lore-size: 15.5px; font-size: 15.5px; line-height: 1.32;';
  return '--deck-lore-size: 16.5px; font-size: 16.5px; line-height: 1.34;';
}
